'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';

import { useServiceLayerTracking } from '../../../Tracking/hooks/useServiceLayerTracking';
import { useEcoLabelItemTracking } from './Tracking/useEcoLabelItemTracking/useEcoLabelItemTracking';
import {
  isValidEcoLabelItem,
  type EcoLabelItemValidationProps,
} from '../../helper/ecoLabelValidator/ecoLabelValidator';

export type EcoLabelItemProps = EcoLabelItemValidationProps & {
  /**
   * If `ecoLabelData['text']` is `null`, this fallback is used instead.
   */
  fallbackHeadline?: string;
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabelItem = ({
  fallbackHeadline,
  ecoLabelData,
  mode = 'normal',
}: EcoLabelItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { trackServiceLayerClick } = useServiceLayerTracking();
  const { trackEcoLabelDisplayOverlayed } = useEcoLabelItemTracking();

  if (!isValidEcoLabelItem({ ...ecoLabelData, mode })) {
    return null;
  }
  const { iconUrl, license, serviceLayerId, text } = ecoLabelData;

  const openDialogHandler = (id: string) => {
    trackServiceLayerClick(id);
    trackEcoLabelDisplayOverlayed(text || fallbackHeadline || id);

    setIsModalOpen(true);
  };

  const closeDialogHandler = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={mode === 'normal' ? 'flex w-full items-center gap-1.5 text-left' : 'w-4'}
        onClick={serviceLayerId ? () => openDialogHandler(serviceLayerId) : undefined}
        disabled={!serviceLayerId}
      >
        {iconUrl && (
          <Image
            width={mode === 'normal' ? 52 : 32}
            height={mode === 'normal' ? 52 : 32}
            alt={text || ''}
            src={iconUrl}
            unoptimized
            className={`object-contain ${mode === 'normal' ? 'max-h-6.5 max-w-6.5' : ''}`}
          />
        )}
        {mode === 'normal' && (text || license) ? (
          <span className="flex flex-col">
            {text && <span className="text-size-body-sm underline">{text}</span>}
            {license && (
              <span className="text-size-body-xs text-color-text-darktransparent">{license}</span>
            )}
          </span>
        ) : null}
      </button>
      {serviceLayerId && (
        <Dialog isOpen={isModalOpen} onClose={closeDialogHandler} variant="bottomSheet">
          <DialogHeader onClose={closeDialogHandler}>
            {mode === 'normal'
              ? text || fallbackHeadline
              : formatMessage({
                  id: 'ecoLabel.productStandard.dialog.title',
                  defaultMessage: 'Productstandard',
                })}
          </DialogHeader>
          <DialogContent>
            <ServiceLayer serviceLayerId={serviceLayerId} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
